import { SystemEntity } from '@erp/shared-types';
import type { GetDocumentPreviewResponse } from '@erp/rpc';
import { cn } from '@/lib/utils';
import { FileTextIcon } from '@phosphor-icons/react';
import { useDocumentPreview } from '@/api/rpc/hooks/core/useDocuments';
import { Badge } from '@/components/ui/badge';
import { T, Var } from 'gt-react';

interface CoreDocumentPreviewProps {
  entityType: SystemEntity; // Required by preview registry
  entityId: string;
  className?: string;
}

type DocumentPreviewMetadata = GetDocumentPreviewResponse['metadata'];

export function CoreDocumentPreview({
  entityId,
  className,
}: CoreDocumentPreviewProps) {
  const { data, isLoading, error } = useDocumentPreview(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <FileTextIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T>Document</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T>Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <FileTextIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T>Document</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T>Unable to load document preview</T>
        </p>
      </div>
    );
  }

  const metadata = (data as { metadata?: DocumentPreviewMetadata }).metadata;

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <FileTextIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold truncate">{data.fileName}</h4>
      </div>

      <div className="space-y-2 text-xs">
        <div className="flex flex-wrap gap-1.5">
          {metadata?.isSplitParent && (
            <Badge variant="secondary" className="text-[10px]">
              <T>
                Split Parent (<Var>{metadata.childDocumentsCount}</Var>)
              </T>
            </Badge>
          )}
          {metadata?.isSplitChild && (
            <Badge variant="outline" className="text-[10px]">
              <T>Split Child</T>
            </Badge>
          )}
          {metadata?.isTransformedFile && (
            <Badge variant="outline" className="text-[10px]">
              <T>Transformed File</T>
            </Badge>
          )}
        </div>
        <div>
          <span className="text-muted-foreground">
            <T>Type:</T>
          </span>
          <span className="font-medium">
            {data.mimeType || (
              <T context="fallback label shown when the document MIME type is not available">
                Unknown
              </T>
            )}
          </span>
        </div>
        {metadata?.classificationType && (
          <div>
            <span className="text-muted-foreground">
              <T>Classification:</T>
            </span>
            <span className="font-medium">{metadata.classificationType}</span>
          </div>
        )}
        {typeof metadata?.classificationConfidence === 'number' && (
          <div>
            <span className="text-muted-foreground">
              <T>Confidence:</T>
            </span>
            <span className="font-medium">
              {Math.round(metadata.classificationConfidence * 100)}%
            </span>
          </div>
        )}
        {metadata?.classificationMethod && (
          <div>
            <span className="text-muted-foreground">
              <T>Method:</T>
            </span>
            <span className="font-medium">{metadata.classificationMethod}</span>
          </div>
        )}
        {metadata?.parentDocumentId && (
          <div>
            <span className="text-muted-foreground">
              <T>Parent:</T>
            </span>
            <span className="font-medium font-mono">
              {metadata.parentDocumentId.slice(0, 8)}
            </span>
          </div>
        )}
        <div>
          <span className="text-muted-foreground">
            <T>Status:</T>
          </span>
          <span className="font-medium">
            <T>Ready to preview</T>
          </span>
        </div>
      </div>
    </div>
  );
}
